import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { isFeedFresh, shouldSkipCachedFeed } from '../../utils/feedFreshness';

export const fetchCurrentPeriodLeaderboard = createAsyncThunk(
  'leaderboard/fetchCurrentPeriodLeaderboard',
  async ({ page = 1, limit = 50, force = false } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/leaderboard/current-period`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch leaderboard');
    }
  },
  {
    condition: ({ page = 1, limit = 50, force = false } = {}, { getState }) => {
      const section = getState().leaderboard.currentPeriodLeaderboard;
      return !shouldSkipCachedFeed({
        force,
        fetchedAt: section.lastFetchedAt,
        hasData: section.lastFetchedAt != null,
        page,
        limit,
        cachedPage: section.lastFetchPage,
        cachedLimit: section.lastFetchLimit,
      });
    },
  }
);

export const fetchTopUsersForRewards = createAsyncThunk(
  'leaderboard/fetchTopUsersForRewards',
  async ({ limit = 10, force = false } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/leaderboard/top-users`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch top users');
    }
  },
  {
    condition: ({ force = false } = {}, { getState }) => {
      if (force) return true;
      const section = getState().leaderboard.topUsersForRewards;
      return !(section.lastFetchedAt != null && isFeedFresh(section.lastFetchedAt));
    },
  }
);

export const fetchUserLeaderboardPosition = createAsyncThunk(
  'leaderboard/fetchUserLeaderboardPosition',
  async (arg, { rejectWithValue }) => {
    const userId = typeof arg === 'object' ? arg.userId : arg;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/leaderboard/user-position/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch user position');
    }
  },
  {
    condition: (arg, { getState }) => {
      const userId = typeof arg === 'object' ? arg.userId : arg;
      const force = typeof arg === 'object' ? Boolean(arg.force) : false;
      if (!userId) return false;
      if (force) return true;
      const section = getState().leaderboard.userPosition;
      if (String(section.userId) !== String(userId)) return true;
      return section.data == null || !isFeedFresh(section.lastFetchedAt);
    },
  }
);

export const fetchLeaderboardStats = createAsyncThunk(
  'leaderboard/fetchLeaderboardStats',
  async (arg = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/leaderboard/stats`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch leaderboard stats');
    }
  },
  {
    condition: (arg = {}, { getState }) => {
      const force = arg?.force ?? false;
      if (force) return true;
      const section = getState().leaderboard.stats;
      return section.data == null || !isFeedFresh(section.lastFetchedAt);
    },
  }
);

// Initial state
const initialState = {
  currentPeriodLeaderboard: {
    data: [],
    loading: false,
    error: null,
    lastFetchedAt: null,
    lastFetchPage: 1,
    lastFetchLimit: 50,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      hasNext: false,
      hasPrev: false
    }
  },
  topUsersForRewards: {
    data: [],
    loading: false,
    error: null,
    lastFetchedAt: null,
  },
  userPosition: {
    data: null,
    loading: false,
    error: null,
    lastFetchedAt: null,
    userId: null,
  },
  stats: {
    data: null,
    loading: false,
    error: null,
    lastFetchedAt: null,
  }
};

// Slice
const leaderboardSlice = createSlice({
  name: 'leaderboard',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.currentPeriodLeaderboard.error = null;
      state.topUsersForRewards.error = null;
      state.userPosition.error = null;
      state.stats.error = null;
    },
    clearCurrentPeriodData: (state) => {
      state.currentPeriodLeaderboard.data = [];
      state.currentPeriodLeaderboard.pagination = initialState.currentPeriodLeaderboard.pagination;
    },
    clearTopUsersData: (state) => {
      state.topUsersForRewards.data = [];
    },
    clearUserPositionData: (state) => {
      state.userPosition.data = null;
    },
    clearStatsData: (state) => {
      state.stats.data = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPeriodLeaderboard.pagination.currentPage = action.payload;
    },
    resetLeaderboardState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentPeriodLeaderboard.pending, (state) => {
        state.currentPeriodLeaderboard.loading = true;
        state.currentPeriodLeaderboard.error = null;
      })
      .addCase(fetchCurrentPeriodLeaderboard.fulfilled, (state, action) => {
        state.currentPeriodLeaderboard.loading = false;
        state.currentPeriodLeaderboard.data = action.payload.leaderboard || [];
        state.currentPeriodLeaderboard.pagination = action.payload.pagination || initialState.currentPeriodLeaderboard.pagination;
        const page = action.meta?.arg?.page ?? 1;
        const limit = action.meta?.arg?.limit ?? state.currentPeriodLeaderboard.lastFetchLimit;
        if (page === 1) {
          state.currentPeriodLeaderboard.lastFetchedAt = Date.now();
          state.currentPeriodLeaderboard.lastFetchPage = page;
          state.currentPeriodLeaderboard.lastFetchLimit = limit;
        }
      })
      .addCase(fetchCurrentPeriodLeaderboard.rejected, (state, action) => {
        state.currentPeriodLeaderboard.loading = false;
        state.currentPeriodLeaderboard.error = action.payload;
      })
      .addCase(fetchTopUsersForRewards.pending, (state) => {
        state.topUsersForRewards.loading = true;
        state.topUsersForRewards.error = null;
      })
      .addCase(fetchTopUsersForRewards.fulfilled, (state, action) => {
        state.topUsersForRewards.loading = false;
        state.topUsersForRewards.data = action.payload.topUsers || [];
        state.topUsersForRewards.lastFetchedAt = Date.now();
      })
      .addCase(fetchTopUsersForRewards.rejected, (state, action) => {
        state.topUsersForRewards.loading = false;
        state.topUsersForRewards.error = action.payload;
      })
      .addCase(fetchUserLeaderboardPosition.pending, (state) => {
        state.userPosition.loading = true;
        state.userPosition.error = null;
      })
      .addCase(fetchUserLeaderboardPosition.fulfilled, (state, action) => {
        state.userPosition.loading = false;
        state.userPosition.data = action.payload.user || null;
        state.userPosition.lastFetchedAt = Date.now();
        const userId =
          typeof action.meta.arg === 'object' ? action.meta.arg.userId : action.meta.arg;
        if (userId) state.userPosition.userId = userId;
      })
      .addCase(fetchUserLeaderboardPosition.rejected, (state, action) => {
        state.userPosition.loading = false;
        state.userPosition.error = action.payload;
      })
      .addCase(fetchLeaderboardStats.pending, (state) => {
        state.stats.loading = true;
        state.stats.error = null;
      })
      .addCase(fetchLeaderboardStats.fulfilled, (state, action) => {
        state.stats.loading = false;
        state.stats.data = action.payload.stats || null;
        state.stats.lastFetchedAt = Date.now();
      })
      .addCase(fetchLeaderboardStats.rejected, (state, action) => {
        state.stats.loading = false;
        state.stats.error = action.payload;
      });
  }
});

// Actions
export const {
  clearErrors,
  clearCurrentPeriodData,
  clearTopUsersData,
  clearUserPositionData,
  clearStatsData,
  setCurrentPage,
  resetLeaderboardState
} = leaderboardSlice.actions;

// Selectors
export const selectCurrentPeriodLeaderboard = (state) => state.leaderboard.currentPeriodLeaderboard;
export const selectTopUsersForRewards = (state) => state.leaderboard.topUsersForRewards;
export const selectUserPosition = (state) => state.leaderboard.userPosition;
export const selectLeaderboardStats = (state) => state.leaderboard.stats;

export const selectCurrentPeriodData = (state) => state.leaderboard.currentPeriodLeaderboard.data;
export const selectCurrentPeriodLoading = (state) => state.leaderboard.currentPeriodLeaderboard.loading;
export const selectCurrentPeriodError = (state) => state.leaderboard.currentPeriodLeaderboard.error;
export const selectCurrentPeriodPagination = (state) => state.leaderboard.currentPeriodLeaderboard.pagination;

export const selectTopUsersData = (state) => state.leaderboard.topUsersForRewards.data;
export const selectTopUsersLoading = (state) => state.leaderboard.topUsersForRewards.loading;
export const selectTopUsersError = (state) => state.leaderboard.topUsersForRewards.error;

export const selectUserPositionData = (state) => state.leaderboard.userPosition.data;
export const selectUserPositionLoading = (state) => state.leaderboard.userPosition.loading;
export const selectUserPositionError = (state) => state.leaderboard.userPosition.error;

export const selectStatsData = (state) => state.leaderboard.stats.data;
export const selectStatsLoading = (state) => state.leaderboard.stats.loading;
export const selectStatsError = (state) => state.leaderboard.stats.error;

export default leaderboardSlice.reducer; 